import type { LinkWorkSupportedType } from '@/lib/utils/link-work-module-types'
import type { KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { SmartTimeInput } from '@/components/shared/SmartTimeInput'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { SplitTimeEntryDialog } from '@/components/time-entry/SplitTimeEntryDialog'
import { TimeEntryTagging } from '@/components/time-entry/TimeEntryTagging'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateInputPicker } from '@/components/ui/date-input-picker'
import {
  DIALOG_FIELD_AREA_CLASS,
  DIALOG_FIELD_CLASS,
  DialogFieldRow,
  DialogFold,
  DialogMoreOptions,
  joinSummary,
} from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateTimeEntryMutation,
  useDeleteTimeEntryMutation,
  useUpdateTimeEntryMutation,
} from '@/hooks/mutations/useTimeEntryMutations'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import {
  useMyPeriodStatusQuery,
  usePayPeriodsQuery,
} from '@/hooks/queries/usePayPeriodsQuery'
import { useTimeEntryQuery } from '@/hooks/queries/useTimeEntriesQuery'
import { useCalendarDay } from '@/hooks/useCalendarDay'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useReplaceTagsForTimeEntry } from '@/hooks/useTimeEntryTags'
import { useTimeFormat } from '@/hooks/useTimeFormat'
import { useTimeTimezone } from '@/hooks/useTimeTimezone'
import { ModuleSearchResult } from '@/lib/api/search'
import { cn } from '@/lib/utils'
import {
  getLinkWorkAllowedModuleTypes,
  LINK_WORK_SUPPORTED_TYPES,
} from '@/lib/utils/link-work-module-types'
import RoleSelector from '@/pages/team/_components/RoleSelector'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { getModifierKeyLabel } from '@/utils/keyboard-utils'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ArrowsSplitIcon,
  CircleNotchIcon,
  ClockIcon,
  GlobeIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import type {
  CreateTimeEntry,
  TimeEntry,
  TimeEntryLinkedModule,
} from '@scrambl/shared'
import type { ClockOccurrence } from '@scrambl/shared/zoned-time'
import {
  BillingConfigurationEnforcementModeEnum,
  calendarDateFromPicker,
  calendarDateFromStorage,
  calendarDateToPicker,
  calendarDateToStorage,
  ResourceModuleSettingsSchema,
  timeEntryMinutes,
  TimeEntryModuleSettingsSchema,
  TimeEntryStatusEnum,
  TimeEntryTypeEnum,
  UserPayPeriodStatusEnum,
} from '@scrambl/shared'
import {
  endClockFromHours,
  resolveEntryClock,
  resolveEntryClockRange,
} from '@scrambl/shared/zoned-time'

import { initialEntryClocks } from './time-entry-editor-clock'

const TIME_ENTRY_MODULE_LINK_TYPES = [
  ...LINK_WORK_SUPPORTED_TYPES,
  'RESOURCE',
  'TEAM',
] as const
const TIME_ENTRY_MODULE_LINK_TYPE_SET = new Set<string>(
  TIME_ENTRY_MODULE_LINK_TYPES,
)

const timeEntrySchema = z.object({
  date: z.date({
    required_error: 'Date is required',
  }),
  startDate: z.date(),
  endDate: z.date(),
  startOccurrence: z.enum(['earlier', 'later']).optional(),
  endOccurrence: z.enum(['earlier', 'later']).optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  hours: z.string().optional(),
  moduleType: z.enum(TIME_ENTRY_MODULE_LINK_TYPES).optional(),
  moduleId: z.string().optional(),
  roleId: z.string().uuid().optional().nullable(),
  description: z.string().optional(),
  billable: z.boolean().default(true),
  status: z.string().default('DRAFT'),
})
type TimeEntryFormData = z.infer<typeof timeEntrySchema>
type TimeEntryModuleLinkType =
  | TimeEntryFormData['moduleType']
  | LinkWorkSupportedType
const DEFAULT_BILL_TO_TYPES = [...LINK_WORK_SUPPORTED_TYPES, 'SUBTASK']

interface CreateTimeEntryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entry?: TimeEntry | null
  /** Calendar day from a date picker, never an event start instant. */
  defaultDate?: Date
  defaultStartTime?: Date
  defaultEndTime?: Date
  defaultModuleType?: TimeEntryModuleLinkType
  defaultModuleId?: string
  defaultModuleName?: string
  defaultRoleId?: string
  targetUserId?: string
  onSaved?: (entry: TimeEntry) => void
  disableNestedSplitDialog?: boolean
}

export function CreateTimeEntryDialog({
  open,
  onOpenChange,
  entry,
  defaultDate,
  defaultStartTime,
  defaultEndTime,
  defaultModuleType,
  defaultModuleId,
  defaultModuleName,
  defaultRoleId,
  targetUserId,
  onSaved,
  disableNestedSplitDialog = false,
}: CreateTimeEntryDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableBillingRoleCount, setAvailableBillingRoleCount] = useState<
    number | null
  >(null)
  // Split of an already-saved entry (edit mode). The validated edits are held
  // here and only saved when the user confirms the split, right before the
  // split itself runs. Snapshotted so the split dialog doesn't re-seed.
  const [existingSplitData, setExistingSplitData] = useState<{
    updatePayload: Partial<CreateTimeEntry> | null
    totalMinutes: number
    linkedModules: TimeEntryLinkedModule[]
  } | null>(null)
  // Extra "Link to" rows beyond the primary one (create mode only). A null
  // slot is an empty row waiting for a selection.
  const [extraLinkedModules, setExtraLinkedModules] = useState<
    Array<TimeEntryLinkedModule | null>
  >([])
  // Draft split flow: the validated payload is held here while the split
  // dialog is open; the entry is only created when the user confirms the split.
  // Everything is snapshotted (entry + modules) so the split dialog's props
  // keep a stable identity and it doesn't re-init the user's split edits.
  const [draftSplitData, setDraftSplitData] = useState<{
    payload: Partial<CreateTimeEntry>
    totalMinutes: number
    draftEntry: TimeEntry
    linkedModules: TimeEntryLinkedModule[]
  } | null>(null)
  const [isDraftSplitOpen, setIsDraftSplitOpen] = useState(false)
  const [selectedModuleName, setSelectedModuleName] = useState<
    string | undefined
  >(undefined)
  const [selectedModuleProjectId, setSelectedModuleProjectId] = useState<
    string | null
  >(null)
  const [selectedModuleEntityId, setSelectedModuleEntityId] = useState<
    string | null
  >(null)
  const [pendingTaggedUserIds, setPendingTaggedUserIds] = useState<string[]>([])
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  // End date is folded until the entry spans days or the user opts in.
  const [isEndDateOpen, setIsEndDateOpen] = useState(false)
  const { checkFeature } = useSubscriptionFeatures()
  const { getCurrentUserMembership } = useOrganizationStore()
  const currentMembership = getCurrentUserMembership()
  const { activeTimezone, userTimezone } = useTimeTimezone()
  const { user } = useAuth()
  const ownerId = targetUserId ?? entry?.userId ?? user?.id
  const personalToday = useCalendarDay(userTimezone)
  const canCheckFutureDate = !ownerId || ownerId === user?.id
  const [editorTimezone, setEditorTimezone] = useState(activeTimezone)
  const clockOriginalRef = useRef({
    startTime: entry?.startTime ?? defaultStartTime,
    endTime: entry?.endTime ?? defaultEndTime,
  })
  const initialHoursRef = useRef('')
  const hoursEditedRef = useRef(false)
  const isActiveTimer =
    entry?.entryType === TimeEntryTypeEnum.TIMER && !entry.endTime
  const { data: settingsData } = useOrganizationSettingsQuery()
  const {
    formatTime,
    parseTime: parseTimeInput,
    getTimeFormat,
  } = useTimeFormat()

  const { data: entryDetails } = useTimeEntryQuery(entry?.id, {
    enabled: open && !!entry?.id,
  })
  const resolvedEntry = entryDetails ?? entry ?? null
  // Submitted/approved entries are awaiting or past review and cannot be edited.
  const isEntryReadOnly =
    resolvedEntry?.status === TimeEntryStatusEnum.SUBMITTED ||
    resolvedEntry?.status === TimeEntryStatusEnum.APPROVED
  const linkedModules = resolvedEntry?.linkedModules ?? []
  const usesRealtimeLinkedItems = Boolean(resolvedEntry?.timerSessionId)

  const timeSettings = TimeEntryModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TIME_ENTRY || {},
  )
  const teamSettings = ResourceModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TEAM || {},
  )
  const billingConfiguration = teamSettings.billingConfiguration
  const linkWorkAllowedModuleTypes = getLinkWorkAllowedModuleTypes(
    timeSettings.allowedBillToTypes &&
      timeSettings.allowedBillToTypes.length > 0
      ? timeSettings.allowedBillToTypes
      : DEFAULT_BILL_TO_TYPES,
  )
  const allowManualEntry = timeSettings.allowManualEntry
  const allowTimerEntry = timeSettings.allowTimerEntry

  const createTimeEntryMutation = useCreateTimeEntryMutation()
  const deleteTimeEntryMutation = useDeleteTimeEntryMutation()
  const updateTimeEntryMutation = useUpdateTimeEntryMutation()
  // The base entry created by the draft split's beforeSplit. Kept so a failed
  // split can be retried without creating a duplicate, and rolled back if the
  // user cancels after the entry was already created.
  const createdDraftEntryRef = useRef<TimeEntry | null>(null)
  const replaceTagsForTimeEntryMutation = useReplaceTagsForTimeEntry()

  const form = useForm<TimeEntryFormData>({
    resolver: zodResolver(timeEntrySchema),
    defaultValues: {
      ...initialEntryClocks({
        entry,
        defaultDate,
        defaultStartTime,
        defaultEndTime,
        timezone: editorTimezone,
        formatHours: formatTime,
      }),
      moduleType: (entry?.moduleType ||
        defaultModuleType) as TimeEntryModuleLinkType,
      moduleId: entry?.moduleId || defaultModuleId || '',
      description: entry?.description || '',
      billable: entry?.billable ?? timeSettings.defaultBillable,
      status:
        entry?.status ||
        (timeSettings.requireApproval
          ? timeSettings.defaultStatus
          : 'APPROVED'),
    },
  })

  const resetKey = [
    open ? 'open' : 'closed',
    entry?.id ?? 'new',
    defaultDate?.getTime() ?? '',
    defaultStartTime?.getTime() ?? '',
    defaultEndTime?.getTime() ?? '',
    defaultModuleType ?? '',
    defaultModuleId ?? '',
    defaultModuleName ?? '',
    defaultRoleId ?? '',
    timeSettings.defaultBillable,
    timeSettings.defaultStatus,
    timeSettings.requireApproval,
  ].join('|')
  const lastResetKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      lastResetKeyRef.current = null
      return
    }

    if (lastResetKeyRef.current === resetKey) {
      return
    }

    lastResetKeyRef.current = resetKey
    setEditorTimezone(activeTimezone)
    clockOriginalRef.current = {
      startTime: entry?.startTime ?? defaultStartTime,
      endTime: entry?.endTime ?? defaultEndTime,
    }
    hoursEditedRef.current = false
    initialHoursRef.current = initialEntryClocks({
      entry,
      defaultDate,
      defaultStartTime,
      defaultEndTime,
      timezone: activeTimezone,
      formatHours: formatTime,
    }).hours

    form.reset({
      ...initialEntryClocks({
        entry,
        defaultDate,
        defaultStartTime,
        defaultEndTime,
        timezone: activeTimezone,
        formatHours: formatTime,
      }),
      moduleType: (entry?.moduleType ||
        defaultModuleType) as TimeEntryModuleLinkType,
      moduleId: entry?.moduleId || defaultModuleId || '',
      roleId: entry?.roleId || defaultRoleId || undefined,
      description: entry?.description || '',
      billable: entry?.billable ?? timeSettings.defaultBillable,
      status:
        entry?.status ||
        (timeSettings.requireApproval
          ? timeSettings.defaultStatus
          : 'APPROVED'),
    })
    setSelectedModuleName(entry?.moduleName ?? defaultModuleName ?? undefined)
    setSelectedModuleProjectId(null)
    setSelectedModuleEntityId(null)
    setAvailableBillingRoleCount(null)
    setPendingTaggedUserIds([])
    setExtraLinkedModules([])
    setDraftSplitData(null)
    setIsDraftSplitOpen(false)
    setExistingSplitData(null)
    setIsDescriptionOpen(false)
    setIsMoreOpen(false)
    setIsEndDateOpen(false)
    createdDraftEntryRef.current = null
  }, [
    activeTimezone,
    defaultDate,
    defaultStartTime,
    defaultEndTime,
    defaultModuleId,
    defaultModuleType,
    defaultModuleName,
    defaultRoleId,
    entry,
    form,
    formatTime,
    open,
    resetKey,
    timeSettings.defaultBillable,
    timeSettings.defaultStatus,
    timeSettings.requireApproval,
  ])

  // Check if selected date falls in a submitted/approved/closed pay period
  const watchedDate = form.watch('date')
  const { data: allPeriodsData } = usePayPeriodsQuery({
    limit: 200,
    enabled: open,
  })
  const matchingPeriod = (() => {
    if (!watchedDate || !allPeriodsData?.data) return null
    const selected = calendarDateFromPicker(watchedDate)
    return (
      allPeriodsData.data.find(period => {
        const start = calendarDateFromStorage(period.startDate)
        const end = calendarDateFromStorage(period.endDate)
        if (!start || !end) return false
        return selected >= start && selected <= end
      }) ?? null
    )
  })()
  const { data: matchingUserPeriod } = useMyPeriodStatusQuery(
    matchingPeriod?.id,
    {
      enabled: open && !!matchingPeriod?.id && !targetUserId,
    },
  )
  const periodWarning = (() => {
    if (targetUserId || !matchingUserPeriod) return null
    if (matchingUserPeriod.status === UserPayPeriodStatusEnum.SUBMITTED) {
      // Adding a brand-new entry to a submitted period is only blocked when the
      // org enables "block new entries after submit". Editing/moving an existing
      // entry into a submitted period is always blocked.
      if (!entry && !timeSettings.blockNewEntriesAfterSubmit) {
        return null
      }
      return 'Your timesheet for this period has been submitted. Recall your submission to make changes.'
    }
    if (matchingUserPeriod.status === UserPayPeriodStatusEnum.APPROVED) {
      return 'Your timesheet for this period has been approved. Contact your admin to reopen it.'
    }
    if (matchingUserPeriod.status === UserPayPeriodStatusEnum.CLOSED) {
      return 'Your timesheet for this period has been closed. No changes can be made.'
    }
    return null
  })()

  const clockValues = form.watch()
  const clockInputs = (data: TimeEntryFormData) => ({
    start: {
      day: calendarDateFromPicker(data.startDate),
      clock: data.startTime ?? '',
      timezone: editorTimezone,
      original: clockOriginalRef.current.startTime,
      occurrence: data.startOccurrence,
    },
    end: {
      day: calendarDateFromPicker(data.endDate),
      clock: data.endTime ?? '',
      timezone: editorTimezone,
      original: clockOriginalRef.current.endTime,
      occurrence: data.endOccurrence,
    },
  })
  const currentClocks = clockInputs(clockValues)
  const startClock = resolveEntryClock(currentClocks.start)
  const endClock = resolveEntryClock(currentClocks.end)
  const pausedSeconds = entry?.pausedDuration ?? 0
  const sameInstant = (
    left: Date | null,
    right: Date | string | null | undefined,
  ) =>
    left && right
      ? left.getTime() === new Date(right).getTime()
      : !left && !right
  const originalRange = (start: Date | null, end: Date | null) =>
    !!entry &&
    sameInstant(start, entry.startTime) &&
    sameInstant(end, entry.endTime)
  // Clock dates stay hidden while they match the timesheet date; they only
  // surface when the entry actually spans days or the user opts in.
  const timesheetDay = clockValues.date
    ? calendarDateFromPicker(clockValues.date)
    : null
  const isStartDateShown = currentClocks.start.day !== timesheetDay
  const isEndDateShown =
    isEndDateOpen || currentClocks.end.day !== currentClocks.start.day
  const timeEntryHint = isActiveTimer
    ? 'This timer is running. Stop it before changing its clock times or hours.'
    : allowManualEntry && allowTimerEntry
      ? 'Enter start and end times, or total hours. Hours exclude recorded pauses.'
      : allowManualEntry
        ? 'Enter total hours for this entry.'
        : 'Enter start and end times.'

  const syncTimeFields = () => {
    if (isActiveTimer) return
    const data = form.getValues()
    if (!data.startTime || !data.endTime) return
    const inputs = clockInputs(data)
    const range = resolveEntryClockRange(
      inputs.start,
      inputs.end,
      pausedSeconds,
    )
    if (originalRange(range.start.instant, range.end.instant)) {
      form.setValue('hours', initialHoursRef.current, { shouldDirty: true })
      hoursEditedRef.current = false
      return
    }
    if (range.error) return
    form.setValue('hours', formatTime(range.hours), { shouldDirty: true })
    hoursEditedRef.current = false
  }
  const handleTimeBlur = () => syncTimeFields()
  const handleTimeChange = (name: 'startTime' | 'endTime', value: string) => {
    if (name === 'endTime')
      clockOriginalRef.current.endTime = entry?.endTime ?? defaultEndTime
    form.setValue(name, value, { shouldDirty: true, shouldTouch: true })
    form.setValue(
      name === 'startTime' ? 'startOccurrence' : 'endOccurrence',
      undefined,
    )
    syncTimeFields()
    if (
      name === 'startTime' &&
      value &&
      !form.getValues('endTime') &&
      form.getValues('hours')
    ) {
      hoursEditedRef.current = true
      handleHoursBlur()
    }
  }
  const handleHoursBlur = () => {
    if (isActiveTimer || !hoursEditedRef.current) return
    const data = form.getValues()
    if (!data.hours?.trim()) {
      hoursEditedRef.current = false
      return
    }
    const hours = parseTimeInput(data.hours ?? '')
    if (!Number.isFinite(hours) || hours <= 0) return
    const inputs = clockInputs(data)
    const start = resolveEntryClock(inputs.start)
    const end = resolveEntryClock(inputs.end)
    if (
      originalRange(start.instant, end.instant) &&
      hours === parseTimeInput(initialHoursRef.current)
    ) {
      hoursEditedRef.current = false
      return
    }
    if (data.startTime) {
      if (!start.instant) return
      try {
        const result = endClockFromHours(
          start.instant,
          hours,
          editorTimezone,
          pausedSeconds,
        )
        // Preserve the exact derived instant even if the clock display hides seconds.
        clockOriginalRef.current.endTime = result.instant
        form.setValue('endDate', calendarDateToPicker(result.day)!, {
          shouldDirty: true,
        })
        form.setValue('endTime', result.clock, { shouldDirty: true })
        form.setValue('endOccurrence', result.occurrence, { shouldDirty: true })
      } catch {
        form.setError('hours', { message: 'Enter a valid duration.' })
        return
      }
    }
    form.setValue('hours', formatTime(hours))
    form.clearErrors('hours')
    hoursEditedRef.current = false
  }

  // Shared by Create/Update and the multi-link "Split Times" flow: runs every
  // guard and returns the entry payload, or null (after toasting) when invalid.
  const validateAndBuildPayload = (
    data: TimeEntryFormData,
  ): Partial<CreateTimeEntry> | null => {
    if (
      !isActiveTimer &&
      timeSettings.requireDescription &&
      !data.description?.trim()
    ) {
      toast.error('Description is required for time entries')
      return null
    }

    if (
      !isActiveTimer &&
      timeSettings.requireModuleLink &&
      (!data.moduleId || !data.moduleType)
    ) {
      toast.error('Please link this time entry to an enabled record')
      return null
    }

    if (
      data.billable &&
      !data.roleId &&
      availableBillingRoleCount !== null &&
      availableBillingRoleCount > 0 &&
      billingConfiguration.enforceBillingConfiguration &&
      billingConfiguration.enforcementMode ===
        BillingConfigurationEnforcementModeEnum.HARD_BLOCK
    ) {
      toast.error('Select a billing role before saving billable time')
      return null
    }

    if (hoursEditedRef.current) {
      handleHoursBlur()
      data = form.getValues()
      if (hoursEditedRef.current) {
        toast.error(
          'Enter valid hours and resolve the start time before saving.',
        )
        return null
      }
    }
    if (
      canCheckFutureDate &&
      !timeSettings.allowFutureEntries &&
      calendarDateFromPicker(data.date) > personalToday
    ) {
      toast.error('Future-dated time entries are not allowed')
      return null
    }

    const moduleLinkPayload =
      data.moduleType && data.moduleId
        ? {
            moduleType: data.moduleType,
            moduleId: data.moduleId,
            ...(selectedModuleName ? { moduleName: selectedModuleName } : {}),
          }
        : entry
          ? { moduleType: null, moduleId: null, moduleName: null }
          : {}
    const rolePayload = data.roleId ? { roleId: data.roleId } : {}
    const entryDate = calendarDateToStorage(calendarDateFromPicker(data.date))
    const common = {
      entryDate,
      description: data.description,
      billable: data.billable,
      ...moduleLinkPayload,
      ...rolePayload,
    }
    if (isActiveTimer) return { ...common, entryType: TimeEntryTypeEnum.TIMER }

    const hasStart = !!data.startTime
    const hasEnd = !!data.endTime
    if (hasStart !== hasEnd) {
      toast.error(
        'Enter both start and end times, or clear both to enter hours only.',
      )
      return null
    }
    let hours = parseTimeInput(data.hours ?? '')
    let start: Date | null = null
    let end: Date | null = null
    if (hasStart && hasEnd) {
      if (!allowTimerEntry) {
        toast.error('Timer-based time entries are disabled')
        return null
      }
      const inputs = clockInputs(data)
      const range = resolveEntryClockRange(
        inputs.start,
        inputs.end,
        pausedSeconds,
      )
      if (
        range.start.instant &&
        range.end.instant &&
        originalRange(range.start.instant, range.end.instant)
      )
        return { ...common, entryType: TimeEntryTypeEnum.TIMER }
      if (range.error || !range.start.instant || !range.end.instant) {
        toast.error(range.error ?? 'Enter valid start and end dates and times.')
        return null
      }
      start = range.start.instant
      end = range.end.instant
      hours = range.hours
    } else {
      if (!allowManualEntry) {
        toast.error('Manual time entries are disabled')
        return null
      }
      if (
        entry?.entryType === TimeEntryTypeEnum.MANUAL &&
        Number.isFinite(hours) &&
        hours === parseTimeInput(initialHoursRef.current)
      ) {
        return { ...common, entryType: TimeEntryTypeEnum.MANUAL }
      }
    }
    if (!Number.isFinite(hours) || hours <= 0) {
      toast.error('Enter a positive number of hours or a valid time range.')
      return null
    }
    const duration =
      timeSettings.roundingInterval > 0
        ? (Math.round((hours * 60) / timeSettings.roundingInterval) *
            timeSettings.roundingInterval) /
          60
        : hours
    if (duration <= 0) {
      toast.error('This duration rounds to zero. Enter more time.')
      return null
    }
    if (
      timeSettings.minimumDuration > 0 &&
      duration * 60 < timeSettings.minimumDuration
    ) {
      toast.error(
        `Time entries must be at least ${timeSettings.minimumDuration} minutes`,
      )
      return null
    }
    if (
      timeSettings.maxHoursPerEntry > 0 &&
      duration > timeSettings.maxHoursPerEntry
    ) {
      toast.error(
        `Time entries cannot exceed ${timeSettings.maxHoursPerEntry} hours`,
      )
      return null
    }
    return {
      ...common,
      entryType: hasStart ? TimeEntryTypeEnum.TIMER : TimeEntryTypeEnum.MANUAL,
      ...(hasStart
        ? { startTime: start, endTime: end }
        : entry?.entryType === TimeEntryTypeEnum.TIMER
          ? { startTime: null, endTime: null }
          : {}),
      duration,
    }
  }

  const onSubmit = async (data: TimeEntryFormData) => {
    if (isEntryReadOnly) {
      toast.error(
        resolvedEntry?.status === TimeEntryStatusEnum.APPROVED
          ? 'This time entry has been approved and can no longer be edited.'
          : 'This time entry has been submitted and can no longer be edited. Recall it first to make changes.',
      )
      return
    }

    if (!checkFeature('time_tracking')) {
      toast.error(
        'Time tracking is not available in your current subscription plan',
      )
      return
    }

    setIsSubmitting(true)
    try {
      const payload = validateAndBuildPayload(data)
      if (!payload) {
        return
      }

      const linkedModulesPayload: TimeEntryLinkedModule[] =
        data.moduleType && data.moduleId
          ? [
              {
                moduleType:
                  data.moduleType as TimeEntryLinkedModule['moduleType'],
                moduleId: data.moduleId,
                ...(selectedModuleName
                  ? { moduleName: selectedModuleName }
                  : {}),
              },
            ]
          : []

      let savedEntry: TimeEntry

      if (entry) {
        const updatePayload = {
          ...payload,
          ...(usesRealtimeLinkedItems
            ? { linkedModules: [] }
            : { linkedModules: linkedModulesPayload }),
        }
        savedEntry = await updateTimeEntryMutation.mutateAsync({
          id: entry.id,
          data: updatePayload,
        })
      } else {
        const createPayload = targetUserId
          ? { ...payload, userId: targetUserId }
          : payload
        savedEntry = await createTimeEntryMutation.mutateAsync({
          data: createPayload as CreateTimeEntry,
        })
      }

      if (!entry) {
        const uniqueTaggedUserIds = Array.from(new Set(pendingTaggedUserIds))
        if (uniqueTaggedUserIds.length > 0) {
          try {
            await replaceTagsForTimeEntryMutation.mutateAsync({
              timeEntryId: savedEntry.id,
              data: { taggedUserIds: uniqueTaggedUserIds },
              suppressErrorToast: true,
            })
          } catch {
            // The entry itself saved; only the tag sync failed (after retries).
            // Surface it explicitly so the members aren't silently dropped.
            const failedCount = uniqueTaggedUserIds.length
            toast.error(
              `Time entry saved, but couldn't tag ${failedCount} member${failedCount === 1 ? '' : 's'}. Reopen the entry to add them.`,
            )
          }
        }
      }

      onOpenChange(false)
      form.reset()
      setPendingTaggedUserIds([])
      onSaved?.(savedEntry)
    } catch (error) {
      // Toast is handled by mutation hooks
    } finally {
      setIsSubmitting(false)
    }
  }

  const onInvalid = () => {
    toast.error('Please fix the highlighted fields before saving')
  }

  // Multi-link create: extra "Link to" rows split the entry's time across the
  // linked items instead of creating one linked entry.
  const canMultiLink = !entry && !disableNestedSplitDialog && !isEntryReadOnly
  const watchedModuleType = form.watch('moduleType')
  const watchedModuleId = form.watch('moduleId')
  const draftLinkedModules: TimeEntryLinkedModule[] = [
    ...(watchedModuleType && watchedModuleId
      ? [
          {
            moduleType:
              watchedModuleType as TimeEntryLinkedModule['moduleType'],
            moduleId: watchedModuleId,
            moduleName: selectedModuleName ?? null,
          },
        ]
      : []),
    ...extraLinkedModules.filter(
      (module): module is TimeEntryLinkedModule => module !== null,
    ),
  ]
  const isMultiLinkCreate = canMultiLink && draftLinkedModules.length > 1

  const handleExtraLinkSelect = (
    index: number,
    result: ModuleSearchResult | null,
  ) => {
    if (!result) {
      setExtraLinkedModules(prev =>
        prev.map((module, idx) => (idx === index ? null : module)),
      )
      return
    }

    if (!TIME_ENTRY_MODULE_LINK_TYPE_SET.has(result.type)) {
      return
    }

    const nextModule: TimeEntryLinkedModule = {
      moduleType: result.type as TimeEntryLinkedModule['moduleType'],
      moduleId: result.id,
      moduleName: result.name ?? null,
    }
    const isDuplicate =
      (form.getValues('moduleType') === result.type &&
        form.getValues('moduleId') === result.id) ||
      extraLinkedModules.some(
        (module, idx) =>
          idx !== index &&
          module?.moduleType === nextModule.moduleType &&
          module?.moduleId === nextModule.moduleId,
      )
    if (isDuplicate) {
      toast.error('That item is already linked.')
      return
    }

    setExtraLinkedModules(prev =>
      prev.map((module, idx) => (idx === index ? nextModule : module)),
    )
  }

  const handleRemoveExtraLink = (index: number) => {
    setExtraLinkedModules(prev => prev.filter((_, idx) => idx !== index))
  }

  // "Split Times": validate the form like Create would, but hold the payload
  // and hand off to the split dialog. Nothing is persisted until the user
  // confirms the split; Cancel returns to this form with everything intact.
  const handleSplitTimes = (data: TimeEntryFormData) => {
    if (!checkFeature('time_tracking')) {
      toast.error(
        'Time tracking is not available in your current subscription plan',
      )
      return
    }

    const payload = validateAndBuildPayload(data)
    if (!payload) {
      return
    }

    const totalMinutes = Math.max(
      1,
      Math.round(Number(payload.duration ?? 0) * 60),
    )
    createdDraftEntryRef.current = null
    setDraftSplitData({
      payload,
      totalMinutes,
      draftEntry: { id: '', ...payload } as TimeEntry,
      linkedModules: draftLinkedModules,
    })
    setIsDraftSplitOpen(true)
  }

  const handleDraftSplitCancel = () => {
    // If the split failed after the base entry was created, cancel means
    // "nothing saved" — roll the base entry back (best effort).
    const createdEntry = createdDraftEntryRef.current
    if (createdEntry) {
      createdDraftEntryRef.current = null
      deleteTimeEntryMutation.mutate(createdEntry.id)
    }
    setIsDraftSplitOpen(false)
    setDraftSplitData(null)
  }

  // Runs when the user confirms the split: create the base entry first, then
  // the split mutation divides it across the linked items.
  const createDraftEntryForSplit = async (): Promise<TimeEntry | null> => {
    if (!draftSplitData) {
      return null
    }
    // A previous attempt already created the base entry (the split itself
    // failed) — reuse it instead of creating a duplicate.
    if (createdDraftEntryRef.current) {
      return createdDraftEntryRef.current
    }
    const createPayload = targetUserId
      ? { ...draftSplitData.payload, userId: targetUserId }
      : draftSplitData.payload
    const savedEntry = await createTimeEntryMutation.mutateAsync({
      data: createPayload as CreateTimeEntry,
    })
    createdDraftEntryRef.current = savedEntry

    const uniqueTaggedUserIds = Array.from(new Set(pendingTaggedUserIds))
    if (uniqueTaggedUserIds.length > 0) {
      try {
        await replaceTagsForTimeEntryMutation.mutateAsync({
          timeEntryId: savedEntry.id,
          data: { taggedUserIds: uniqueTaggedUserIds },
          suppressErrorToast: true,
        })
      } catch {
        const failedCount = uniqueTaggedUserIds.length
        toast.error(
          `Time entry saved, but couldn't tag ${failedCount} member${failedCount === 1 ? '' : 's'}. Reopen the entry to add them.`,
        )
      }
    }

    return savedEntry
  }

  // Edit mode "Split": divide a saved, stopped entry across several items,
  // the same way a timer session is split when it ends.
  const splitAllowedStatuses: string[] = [
    TimeEntryStatusEnum.DRAFT,
    TimeEntryStatusEnum.REJECTED,
    TimeEntryStatusEnum.CHANGES_REQUESTED,
  ]
  const canSplitExisting =
    !!resolvedEntry?.id &&
    !disableNestedSplitDialog &&
    !isEntryReadOnly &&
    !isActiveTimer &&
    splitAllowedStatuses.includes(resolvedEntry.status) &&
    (resolvedEntry.entryType === TimeEntryTypeEnum.TIMER ||
      resolvedEntry.entryType === TimeEntryTypeEnum.MANUAL)
  const isExistingSplitOpen = existingSplitData !== null
  // Read during render: react-hook-form only tracks formState it sees read.
  const isFormDirty = form.formState.isDirty

  const handleSplitExisting = (data: TimeEntryFormData) => {
    if (!resolvedEntry || !canSplitExisting) return
    if (!checkFeature('time_tracking')) {
      toast.error(
        'Time tracking is not available in your current subscription plan',
      )
      return
    }

    const payload = validateAndBuildPayload(data)
    if (!payload) return

    const primaryLink: TimeEntryLinkedModule[] =
      data.moduleType && data.moduleId
        ? [
            {
              moduleType:
                data.moduleType as TimeEntryLinkedModule['moduleType'],
              moduleId: data.moduleId,
              moduleName: selectedModuleName ?? null,
            },
          ]
        : []
    const linkedModulesForSplit =
      !usesRealtimeLinkedItems && linkedModules.length > 1
        ? linkedModules
        : primaryLink
    const totalMinutes =
      typeof payload.duration === 'number'
        ? payload.duration * 60
        : timeEntryMinutes(resolvedEntry)

    setExistingSplitData({
      updatePayload: isFormDirty
        ? {
            ...payload,
            ...(usesRealtimeLinkedItems
              ? { linkedModules: [] }
              : { linkedModules: primaryLink }),
          }
        : null,
      totalMinutes,
      linkedModules: linkedModulesForSplit,
    })
  }

  // Runs when the user confirms the split: persist any pending edits first so
  // the split divides the entry as it appears in the form.
  const saveEditsBeforeSplit = async (): Promise<TimeEntry | null> => {
    if (!resolvedEntry) return null
    const updatePayload = existingSplitData?.updatePayload
    if (!updatePayload) return resolvedEntry
    const savedEntry = await updateTimeEntryMutation.mutateAsync({
      id: resolvedEntry.id,
      data: updatePayload,
    })
    // A retry after a failed split must not re-save the same edits.
    setExistingSplitData(prev =>
      prev ? { ...prev, updatePayload: null } : prev,
    )
    return savedEntry
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault()
        form.handleSubmit(
          isMultiLinkCreate ? handleSplitTimes : onSubmit,
          onInvalid,
        )()
      } else {
        // Prevent plain Enter from submitting the form
        e.preventDefault()
      }
    }
  }

  // DialogPattern owns the dialog content, so the nested-dropdown guard that
  // used to sit on onEscapeKeyDown/onInteractOutside runs here instead: while a
  // Link Work or member-tagging dropdown is open it takes the dismissal.
  const hasOpenNestedDropdown = () =>
    Boolean(
      document.querySelector('[data-link-work-dropdown]') ||
      document.querySelector('[data-member-tagging-dropdown]'),
    )

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && hasOpenNestedDropdown()) {
      return
    }
    onOpenChange(nextOpen)
  }

  const watchedDescription = form.watch('description')
  const watchedBillable = form.watch('billable')
  const moreOptionsSummary = joinSummary([
    watchedBillable ? 'Billable' : 'Non-billable',
    !resolvedEntry?.id && pendingTaggedUserIds.length > 0
      ? `${pendingTaggedUserIds.length} tagged`
      : null,
  ])

  const footer = (
    <>
      {canSplitExisting && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="sm:mr-auto"
          disabled={isSubmitting}
          onClick={form.handleSubmit(handleSplitExisting, onInvalid)}
        >
          <ArrowsSplitIcon className="mr-1.5 h-4 w-4" />
          Split
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        {isEntryReadOnly ? 'Close' : 'Cancel'}
      </Button>
      {!isEntryReadOnly && (
        <Button
          type="button"
          size="sm"
          className="min-w-[100px]"
          title={`${getModifierKeyLabel()}+Enter`}
          disabled={isSubmitting || !!periodWarning}
          onClick={form.handleSubmit(
            isMultiLinkCreate ? handleSplitTimes : onSubmit,
            onInvalid,
          )}
        >
          {isSubmitting ? (
            <>
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : entry ? (
            'Update'
          ) : isMultiLinkCreate ? (
            'Split Times'
          ) : (
            'Create'
          )}
        </Button>
      )}
    </>
  )

  return (
    <>
      {/* Hidden (not closed) while a split dialog is up so the form
          state survives a Cancel and the user lands back here unchanged. */}
      <DialogPattern
        open={open && !isDraftSplitOpen && !isExistingSplitOpen}
        onOpenChange={handleDialogOpenChange}
        title={
          isEntryReadOnly
            ? 'Time Entry'
            : entry
              ? 'Edit Time Entry'
              : 'New Time Entry'
        }
        icon={<ClockIcon className="h-4 w-4 text-primary" weight="duotone" />}
        isForm
        formProps={{
          onSubmit: form.handleSubmit(onSubmit, onInvalid),
          onKeyDown: handleKeyDown,
        }}
        footer={footer}
      >
        <Form {...form}>
          <div className="space-y-4">
            {/* Read-only (submitted/approved) notice */}
            {isEntryReadOnly && (
              <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {resolvedEntry?.status === TimeEntryStatusEnum.APPROVED
                  ? 'This time entry has been approved and can no longer be edited.'
                  : 'This time entry has been submitted and can no longer be edited. Recall it first to make changes.'}
              </div>
            )}

            {/* Period submission warning */}
            {periodWarning && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {periodWarning}
              </div>
            )}

            <fieldset disabled={isEntryReadOnly} className="space-y-4">
              {/* Module Search */}
              <FormField
                control={form.control}
                name="moduleId"
                render={() => (
                  <FormItem className="space-y-1.5">
                    <FormLabel required={timeSettings.requireModuleLink}>
                      Link to
                    </FormLabel>
                    <FormControl>
                      <LinkWorkSelector
                        className="w-full"
                        portalToBody={false}
                        closeOnScroll={false}
                        closeOnSelect
                        allowedModuleTypes={linkWorkAllowedModuleTypes}
                        defaultModule="ALL"
                        value={
                          form.watch('moduleType') && form.watch('moduleId')
                            ? {
                                type: form.watch('moduleType')!,
                                id: form.watch('moduleId')!,
                                name: selectedModuleName,
                              }
                            : null
                        }
                        onSelect={(result: ModuleSearchResult | null) => {
                          if (
                            result &&
                            TIME_ENTRY_MODULE_LINK_TYPE_SET.has(result.type)
                          ) {
                            form.setValue(
                              'moduleType',
                              result.type as TimeEntryModuleLinkType,
                              {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              },
                            )
                            form.setValue('moduleId', result.id, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                            setSelectedModuleName(result.name)
                            setSelectedModuleProjectId(
                              result.type === 'PROJECT'
                                ? result.id
                                : result.projectId || null,
                            )
                            setSelectedModuleEntityId(
                              result.type === 'ENTITY'
                                ? result.id
                                : result.entityId || null,
                            )
                            form.clearErrors('moduleType')
                            form.clearErrors('moduleId')
                          } else {
                            form.setValue('moduleType', undefined as any, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                            form.setValue('moduleId', '', {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                            setSelectedModuleName(undefined)
                            setSelectedModuleProjectId(null)
                            setSelectedModuleEntityId(null)
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Extra linked items (create mode): time gets split across
                  every linked item via the split dialog on save. */}
              {canMultiLink && (
                <div className="space-y-2">
                  {extraLinkedModules.map((module, index) => (
                    <div
                      key={
                        module
                          ? `${module.moduleType}-${module.moduleId}`
                          : `extra-link-${index}`
                      }
                      className="flex items-center gap-2"
                    >
                      <LinkWorkSelector
                        className="w-full"
                        portalToBody={false}
                        closeOnScroll={false}
                        closeOnSelect
                        allowedModuleTypes={linkWorkAllowedModuleTypes}
                        defaultModule="ALL"
                        value={
                          module
                            ? {
                                type: module.moduleType,
                                id: module.moduleId,
                                name: module.moduleName ?? undefined,
                              }
                            : null
                        }
                        onSelect={result =>
                          handleExtraLinkSelect(index, result)
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveExtraLink(index)}
                        aria-label="Remove linked item"
                      >
                        <XIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {watchedModuleType && watchedModuleId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="justify-start gap-2 px-0 text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setExtraLinkedModules(prev => [...prev, null])
                      }
                    >
                      <PlusIcon className="h-4 w-4" />
                      Link another item
                    </Button>
                  )}
                  {isMultiLinkCreate && (
                    <p className="text-xs text-muted-foreground">
                      Multiple items linked — you&apos;ll choose how to split
                      the time between them next.
                    </p>
                  )}
                </div>
              )}

              {/* Role Selector. Stays up front: a billing role can hard-block
                  the save, so it is never folded away. */}
              {currentMembership?.id && !targetUserId && (
                <FormField
                  control={form.control}
                  name="roleId"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <RoleSelector
                        memberId={currentMembership.id}
                        projectId={selectedModuleProjectId}
                        entityId={
                          selectedModuleProjectId
                            ? null
                            : selectedModuleEntityId
                        }
                        value={field.value}
                        onChange={field.onChange}
                        hideWhenSingle
                        showWarnings={
                          billingConfiguration.enforceBillingConfiguration
                        }
                        enforcementMode={billingConfiguration.enforcementMode}
                        onAvailableRoleCountChange={
                          setAvailableBillingRoleCount
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Date, Time and Hours */}
              <div className="space-y-2">
                <DialogFieldRow>
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <FormLabel>Timesheet date</FormLabel>
                        <FormControl>
                          <DateInputPicker
                            valueSource="picker"
                            ref={field.ref}
                            value={field.value}
                            onChange={date => {
                              field.onChange(date ?? undefined)
                              if (
                                date &&
                                !form.getValues('startTime') &&
                                !form.getValues('endTime')
                              ) {
                                form.setValue('startDate', date)
                                form.setValue('endDate', date)
                              }
                            }}
                            onBlur={field.onBlur}
                            disabledDates={date => {
                              if (calendarDateFromPicker(date) < '1900-01-01')
                                return true
                              if (
                                canCheckFutureDate &&
                                !timeSettings.allowFutureEntries
                              )
                                return (
                                  calendarDateFromPicker(date) > personalToday
                                )
                              return false
                            }}
                            clearable={false}
                            placeholder="Pick date"
                            triggerClassName={DIALOG_FIELD_CLASS}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="hours"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <FormLabel>Hours</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder={
                              getTimeFormat() === 'HH_MM' ? '--:--' : '0.0'
                            }
                            className={DIALOG_FIELD_CLASS}
                            {...field}
                            onBlur={handleHoursBlur}
                            onChange={event => {
                              hoursEditedRef.current = true
                              field.onChange(event)
                            }}
                            value={field.value ?? ''}
                            disabled={
                              isActiveTimer ||
                              (!allowManualEntry && !allowTimerEntry)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </DialogFieldRow>
                {allowTimerEntry && (
                  <div className="space-y-3">
                    {isStartDateShown && (
                      <p className="text-xs text-muted-foreground">
                        Clock times fall on a different day than the timesheet
                        date. Changing the timesheet date keeps them unchanged.
                      </p>
                    )}
                    <DialogFieldRow>
                      {(['start', 'end'] as const).map(side => {
                        const timeName =
                          side === 'start' ? 'startTime' : 'endTime'
                        const label = side === 'start' ? 'Start' : 'End'
                        return (
                          <FormField
                            key={side}
                            control={form.control}
                            name={timeName}
                            render={({ field }) => (
                              <FormItem className="space-y-1.5">
                                <FormLabel>{label} time</FormLabel>
                                <FormControl>
                                  <SmartTimeInput
                                    value={field.value}
                                    onChange={value =>
                                      handleTimeChange(timeName, value)
                                    }
                                    onBlur={handleTimeBlur}
                                    disabled={isActiveTimer}
                                    placeholder="--:--"
                                    className={DIALOG_FIELD_CLASS}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )
                      })}
                    </DialogFieldRow>
                    {(isStartDateShown || isEndDateShown) && (
                      <DialogFieldRow>
                        {(['start', 'end'] as const).map(side => {
                          const isShown =
                            side === 'start' ? isStartDateShown : isEndDateShown
                          if (!isShown)
                            return (
                              <div key={side} className="hidden sm:block" />
                            )
                          const dateName =
                            side === 'start' ? 'startDate' : 'endDate'
                          const occurrenceName =
                            side === 'start'
                              ? 'startOccurrence'
                              : 'endOccurrence'
                          const label = side === 'start' ? 'Start' : 'End'
                          return (
                            <FormField
                              key={side}
                              control={form.control}
                              name={dateName}
                              render={({ field }) => (
                                <FormItem className="space-y-1.5">
                                  <FormLabel>{label} date</FormLabel>
                                  <FormControl>
                                    <DateInputPicker
                                      valueSource="picker"
                                      value={field.value}
                                      ref={field.ref}
                                      onChange={date => {
                                        if (!date) return
                                        field.onChange(date)
                                        form.setValue(occurrenceName, undefined)
                                        if (side === 'end')
                                          clockOriginalRef.current.endTime =
                                            entry?.endTime ?? defaultEndTime
                                        syncTimeFields()
                                      }}
                                      onBlur={field.onBlur}
                                      clearable={false}
                                      disabled={isActiveTimer}
                                      triggerClassName={DIALOG_FIELD_CLASS}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )
                        })}
                      </DialogFieldRow>
                    )}
                    {(['start', 'end'] as const).map(side => {
                      const occurrenceName =
                        side === 'start' ? 'startOccurrence' : 'endOccurrence'
                      const resolved = side === 'start' ? startClock : endClock
                      const label = side === 'start' ? 'Start' : 'End'
                      if (resolved.candidates.length !== 2 && !resolved.error)
                        return null
                      return (
                        <div key={side} className="space-y-1.5">
                          {resolved.candidates.length === 2 && (
                            <label className="block space-y-1 text-xs text-muted-foreground">
                              <span>{label} time occurs twice</span>
                              <select
                                aria-label={`${label} occurrence`}
                                className={cn(DIALOG_FIELD_CLASS, 'w-full')}
                                disabled={isActiveTimer}
                                value={resolved.occurrence ?? ''}
                                onChange={event => {
                                  form.setValue(
                                    occurrenceName,
                                    (event.target.value as ClockOccurrence) ||
                                      undefined,
                                    { shouldDirty: true },
                                  )
                                  syncTimeFields()
                                }}
                              >
                                <option value="">Choose an occurrence</option>
                                {resolved.candidates.map((candidate, index) => (
                                  <option
                                    key={candidate.offset}
                                    value={index === 0 ? 'earlier' : 'later'}
                                  >
                                    {index === 0 ? 'First' : 'Second'}{' '}
                                    occurrence (UTC{candidate.offset})
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          {resolved.error && (
                            <p
                              role="alert"
                              className="text-xs text-destructive"
                            >
                              {resolved.error}
                            </p>
                          )}
                        </div>
                      )
                    })}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <GlobeIcon className="h-3.5 w-3.5" />
                        <span>Clock times in {editorTimezone}</span>
                      </div>
                      {!isEndDateShown && !isActiveTimer && (
                        <DialogFold
                          label="Ends on another day"
                          open={false}
                          onOpenChange={setIsEndDateOpen}
                        >
                          {null}
                        </DialogFold>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{timeEntryHint}</p>
              </div>

              {linkedModules.length > 0 && !usesRealtimeLinkedItems && (
                <div className="space-y-2 rounded-md bg-muted/30 p-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Linked items
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {linkedModules.map(module => (
                      <Badge
                        key={`${module.moduleType}-${module.moduleId}`}
                        variant="secondary"
                      >
                        {module.moduleName || module.moduleType}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Description: the one heavy optional field, folded until asked
                  for — unless the org requires it, when it is never folded. */}
              <DialogFold
                label="Add description"
                open={isDescriptionOpen}
                onOpenChange={setIsDescriptionOpen}
                hasValue={
                  Boolean(watchedDescription) || timeSettings.requireDescription
                }
                disabled={isEntryReadOnly}
              >
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel required={timeSettings.requireDescription}>
                        Description
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What did you work on?"
                          className={cn('resize-none', DIALOG_FIELD_AREA_CLASS)}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </DialogFold>

              <DialogMoreOptions
                open={isMoreOpen}
                onOpenChange={setIsMoreOpen}
                summary={moreOptionsSummary}
                disabled={isEntryReadOnly}
              >
                {resolvedEntry?.id ? (
                  <TimeEntryTagging
                    timeEntryId={resolvedEntry.id}
                    label="Tag members"
                    className="w-full max-w-full"
                    portalToBody={false}
                  />
                ) : (
                  <TimeEntryTagging
                    label="Tag members"
                    className="w-full max-w-full"
                    portalToBody={false}
                    selectedUserIds={pendingTaggedUserIds}
                    onSelectedUserIdsChange={setPendingTaggedUserIds}
                  />
                )}

                {/* Billable Toggle */}
                <FormField
                  control={form.control}
                  name="billable"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-md bg-muted/30 px-3 py-2.5">
                      <div className="space-y-0.5">
                        <FormLabel className="text-xs">Billable</FormLabel>
                        <p className="text-[11px] text-muted-foreground">
                          Mark this time as billable to client
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </DialogMoreOptions>

              {linkedModules.length > 1 &&
                resolvedEntry?.endTime &&
                !usesRealtimeLinkedItems &&
                canSplitExisting && (
                  <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium">Multiple links detected</p>
                        <p className="text-xs text-amber-800/80">
                          Split this time entry into separate records before
                          submitting.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={form.handleSubmit(
                          handleSplitExisting,
                          onInvalid,
                        )}
                      >
                        Split Time
                      </Button>
                    </div>
                  </div>
                )}
            </fieldset>
          </div>
        </Form>
      </DialogPattern>
      {/* Existing-entry split: Cancel returns to the (still intact) edit
          form; pending edits are only saved once the split is confirmed. */}
      {!disableNestedSplitDialog && (
        <SplitTimeEntryDialog
          open={open && isExistingSplitOpen}
          entry={resolvedEntry}
          title="Split Time Entry"
          subtitle="Divide this entry's time across the work it belongs to."
          linkedModulesOverride={existingSplitData?.linkedModules}
          totalMinutesOverride={existingSplitData?.totalMinutes}
          allowedModuleTypes={linkWorkAllowedModuleTypes}
          allowCancel
          onCancel={() => setExistingSplitData(null)}
          onOpenChange={nextOpen => {
            if (!nextOpen) setExistingSplitData(null)
          }}
          beforeSplit={saveEditsBeforeSplit}
          onSplitComplete={() => {
            setExistingSplitData(null)
            form.reset()
            onOpenChange(false)
          }}
        />
      )}
      {/* Draft split: the entry doesn't exist yet — it's created in
          beforeSplit only when the user confirms, so Cancel leaves nothing
          behind and reopens the (still intact) entry form. */}
      {canMultiLink && (
        <SplitTimeEntryDialog
          open={isDraftSplitOpen}
          entry={draftSplitData?.draftEntry ?? null}
          title="Split Time Between Items"
          subtitle="Decide how this entry's time is divided across the linked items."
          linkedModulesOverride={draftSplitData?.linkedModules}
          totalMinutesOverride={draftSplitData?.totalMinutes}
          allowedModuleTypes={linkWorkAllowedModuleTypes}
          allowCancel
          onCancel={handleDraftSplitCancel}
          onOpenChange={setIsDraftSplitOpen}
          beforeSplit={createDraftEntryForSplit}
          onSplitComplete={() => {
            createdDraftEntryRef.current = null
            setIsDraftSplitOpen(false)
            setDraftSplitData(null)
            setExtraLinkedModules([])
            setPendingTaggedUserIds([])
            form.reset()
            onOpenChange(false)
            toast.success('Time entries created')
          }}
        />
      )}
    </>
  )
}
